import { useTranslation } from "react-i18next";

import type { RobotAudience } from "@/entities/robot";
import { formatMoney, formatPercent } from "@/shared/lib";
import { Panel } from "@/shared/ui";

/**
 * Пользователи робота (ТЗ §14–§16).
 *
 * Единственный блок экрана, где ничего не моделируется: распределение и
 * агрегаты считаются по настоящим `user_robots` и `daily_results`. Именно
 * поэтому бэкенд присылает их как `null`, пока покупателей меньше порога, — и
 * тогда вместо статистики по двум покупателям здесь стоит честная строка «ещё
 * рано». ТЗ §15: «если реальной статистики пока нет — блок не показывать».
 *
 * Подиум ТОП-3 отсюда уехал в `widgets/robot-leaderboard` (доработки §5): у
 * него появились аватары, ранги, период и правила приватности, и держать это
 * седьмым флагом видимости внутри этого блока стало нечем оправдать.
 *
 * Счётчик пользователей и суммарный результат гасятся флагами: на экране
 * робота они стоят KPI-плитками под шапкой (`KeyMetricsBlock`), и второй раз
 * здесь были бы поводом сверять, не разошлись ли две одинаковые цифры.
 */
export function AudienceBlock({
  audience,
  showDistribution = true,
  showAum = true,
  showHeadcount = true,
  showProfit = true,
}: {
  audience: RobotAudience;
  showDistribution?: boolean;
  showAum?: boolean;
  /** Счётчик в шапке блока. Выключен там, где он уже стоит KPI-плиткой выше. */
  showHeadcount?: boolean;
  /** Строка «Суммарный результат» — по той же причине. */
  showProfit?: boolean;
}) {
  const { t } = useTranslation();
  const distribution = showDistribution ? audience.distribution : null;

  // Настоящие агрегаты по покупкам (ТЗ §13). `null` означает «покупателей нет»
  // — строка тогда не рисуется вовсе, а не показывает ноль (ТЗ §33).
  const facts: { label: string; value: string }[] = [];
  if (showAum && audience.aum !== null && audience.aum !== undefined) {
    facts.push({
      label: t("robots.analytics.audience.aum"),
      value: formatMoney(audience.aum, { suffix: "USDT" }),
    });
  }
  if (showProfit && audience.user_profit_total !== null && audience.user_profit_total !== undefined) {
    facts.push({
      label: t("robots.analytics.audience.profit"),
      value: formatMoney(audience.user_profit_total, { sign: "always", suffix: "USDT" }),
    });
  }
  if (audience.avg_days !== null && audience.avg_days !== undefined) {
    facts.push({
      label: t("robots.analytics.audience.avgDays"),
      value: t("myRobots.days", { count: audience.avg_days }),
    });
  }

  return (
    <Panel as="section" radius="lg" className="flex flex-col gap-3 p-4">
      <header className="flex items-baseline justify-between gap-3">
        <h2 className="text-body font-semibold text-text-primary">
          {t("robots.analytics.audience.title")}
        </h2>
        {showHeadcount ? (
          <span className="text-caption font-semibold text-text-primary">{audience.count}</span>
        ) : null}
      </header>
      {showHeadcount ? (
        <p className="text-micro font-normal text-text-tertiary">
          {t("robots.analytics.audience.active")}
        </p>
      ) : null}

      {facts.length > 0 ? (
        <dl className="flex flex-col">
          {facts.map((fact, index) => (
            <div
              key={fact.label}
              className={[
                "flex items-center justify-between gap-3 py-2",
                index > 0 ? "border-t border-border-subtle" : "",
              ].join(" ")}
            >
              <dt className="text-caption font-normal text-text-secondary">{fact.label}</dt>
              <dd className="text-caption font-semibold text-text-primary">{fact.value}</dd>
            </div>
          ))}
        </dl>
      ) : null}

      {distribution ? (
        <section className="flex flex-col gap-2">
          <h3 className="text-caption font-semibold text-text-secondary">
            {t("robots.analytics.audience.distributionTitle")}
          </h3>
          {/* Одна полоса из трёх долей: три отдельных индикатора заставляли бы
              складывать их глазами, а сумма и так ровно сто процентов. */}
          <div className="flex h-2 w-full overflow-hidden rounded-full bg-background-raised">
            <span className="bg-status-profit" style={{ width: `${distribution.positive_pct}%` }} />
            <span className="bg-text-tertiary" style={{ width: `${distribution.flat_pct}%` }} />
            <span className="bg-status-loss" style={{ width: `${distribution.negative_pct}%` }} />
          </div>
          <dl className="flex flex-col gap-1">
            {(
              [
                ["positive", distribution.positive_pct, "bg-status-profit"],
                ["flat", distribution.flat_pct, "bg-text-tertiary"],
                ["negative", distribution.negative_pct, "bg-status-loss"],
              ] as const
            ).map(([key, value, tone]) => (
              <div key={key} className="flex items-center justify-between gap-3 text-micro">
                <dt className="flex items-center gap-1.5 font-normal text-text-secondary">
                  <span className={`size-[7px] rounded-full ${tone}`} aria-hidden="true" />
                  {t(`robots.analytics.audience.${key}`)}
                </dt>
                <dd className="font-semibold text-text-primary tabular-nums">
                  {formatPercent(value)}
                </dd>
              </div>
            ))}
          </dl>
        </section>
      ) : (
        <p className="text-micro font-normal text-text-tertiary">
          {t("robots.analytics.audience.pending", { count: audience.min_participants })}
        </p>
      )}
    </Panel>
  );
}
